import React from 'react'
import styles from './perms.module.scss'
import cn from 'classnames'
import perms from 'lib/perms'
import WithTooltip from 'components/shared/WithTooltip'
import questModel from 'components/quests/questModel'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { withDepth } from 'components/filters/useStreamFilters'
import StreamIcon from './StreamIcon'
import { isDirectMessageQuest } from 'lib/directMessages'

export default function Perms(props) {
    const quest = props.quest
    const amAdmin = perms.amQuestOwner(quest)
    const isBsv = props.isBsv
    const isComposer = props.isComposer
    // Stream-breadcrumb variant: the pill chrome lives on the ThinParent crumb container,
    // so this trims inner paddings and swaps the hover treatment onto the caret.
    const isCrumb = props.isCrumb

    const filter = questModel.getFilter(quest)

    // TODO confusing... questModel.isPublic means public:true and not in a team. should be renamed isTreechat
    const isPublic = questModel.isPublic(quest)
    const isHodlocker = gon.HODLOCKER_TEAM_ID && gon.HODLOCKER_TEAM_ID === quest.team_id
    const isTwetch = gon.TWETCH_TEAM_ID && gon.TWETCH_TEAM_ID === quest.team_id

    const isRoot = props.isRoot
    const showTeam = questModel.hasTeam(quest) && !isPublic

    // Drafts can target an existing thread instead of a stream (clip/share flow).
    const threadDestination =
        quest.is_draft && quest.destination_quest_id
            ? {
                  id: quest.destination_quest_id,
                  preview: quest.destination_quest_preview || 'Thread',
              }
            : null

    // A direct message's audience is fixed at its two participants.
    const isDirectMessage = isDirectMessageQuest(quest)

    let permsName
    if (threadDestination) permsName = threadDestination.preview
    else if (isDirectMessage) permsName = 'Direct message'
    else if (showTeam) permsName = quest.team.name
    else if (questModel.isPrivate(quest)) permsName = 'Private'
    else if (!isRoot && isHodlocker) permsName = 'Hodlocker'
    else if (!isRoot && isTwetch) permsName = 'Twetch'
    else if (!isRoot && isPublic) permsName = 'Public'
    else if (questModel.isLinkShare(quest)) permsName = 'Link Access'

    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)

    const onClickStream = ev => {
        return // Let ThinParent handle this
        ev.preventDefault()
        ev.stopPropagation()

        openRight(withDepth(filter))
    }

    const onClickControl = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if ((!isBsv || quest.is_draft) && props.setShowPermsOptions)
            props.setShowPermsOptions(!props.showPermsOptions)
    }

    const canChoosePerms =
        amAdmin &&
        !isDirectMessage &&
        (!isBsv || quest.is_draft) &&
        !!props.setShowPermsOptions &&
        !props.disablePermsControl
    const permsLabelTestId = quest.is_draft ? 'draft-perms-label' : 'thread-perms-label'
    const permsToggleTestId = quest.is_draft ? 'draft-perms-toggle' : 'thread-perms-toggle'

    return (
        <div
            className={cn(
                styles.permsComp,
                isComposer && styles.composerPermsComp,
                props.contextStyles,
            )}
        >
            <WithTooltip
                tip={
                    threadDestination
                        ? 'Publishing into an existing thread'
                        : isDirectMessage
                        ? 'Only the two of you can see this conversation'
                        : `Open ${permsName} channel`
                }
            >
                <div
                    className={cn(styles.permsValue, isComposer && styles.composerPermsValue)}
                    onClick={onClickStream}
                >
                    <div
                        className={cn(
                            styles.permsIconContainer,
                            isComposer && styles.composerPermsIconContainer,
                        )}
                    >
                        {threadDestination ? (
                            <div className={styles.threadDestIcon} data-testid="draft-thread-icon">
                                <i className="fa fa-comments" />
                            </div>
                        ) : (
                            <StreamIcon
                                quest={quest}
                                isRoot={isRoot}
                                contextStyles={styles.permsIcon}
                            />
                        )}
                    </div>
                </div>
            </WithTooltip>

            {canChoosePerms ? (
                <WithTooltip
                    tip={
                        quest.is_draft
                            ? 'Choose channel or thread to publish to'
                            : 'Choose channel to publish to'
                    }
                    contextStyles={styles.withTooltip}
                >
                    <div
                        className={cn(
                            styles.permsControlContainer,
                            isCrumb ? styles.crumbPermsControlContainer : styles.withHover,
                            isComposer && styles.composerPermsControlContainer,
                            isComposer && styles.composerWithHover,
                        )}
                        data-testid={permsToggleTestId}
                        onClick={onClickControl}
                    >
                        <div
                            className={cn(
                                styles.permsLabelContainer,
                                isComposer && styles.composerPermsLabelContainer,
                            )}
                        >
                            <div
                                className={cn(
                                    styles.permsLabel,
                                    isCrumb && styles.crumbPermsLabel,
                                    isComposer && styles.composerPermsLabel,
                                )}
                                data-testid={permsLabelTestId}
                            >
                                {permsName}
                            </div>
                        </div>

                        {(!isBsv || quest.is_draft) && (
                            <div
                                className={cn(
                                    styles.permsControl,
                                    isCrumb && styles.crumbPermsControl,
                                    isComposer && styles.composerPermsControl,
                                )}
                            >
                                <i
                                    className={
                                        isComposer
                                            ? props.showPermsOptions
                                                ? 'fa fa-angle-up'
                                                : 'fa fa-angle-down'
                                            : 'fa fa-angle-down'
                                    }
                                />
                            </div>
                        )}
                    </div>
                </WithTooltip>
            ) : (
                <div
                    className={cn(
                        styles.permsControlContainer,
                        isCrumb && styles.crumbPermsControlContainer,
                        isComposer && styles.composerPermsControlContainer,
                    )}
                >
                    <div
                        className={cn(
                            styles.permsLabelContainer,
                            isComposer && styles.composerPermsLabelContainer,
                        )}
                    >
                        <div
                            className={cn(
                                styles.permsLabel,
                                isCrumb && styles.crumbPermsLabel,
                                isComposer && styles.composerPermsLabel,
                            )}
                            data-testid={permsLabelTestId}
                        >
                            {permsName}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
